const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

export const createMagicOp = ({ point, settings }) => ({
  id: `magic-${Date.now()}-${Math.floor(Math.random() * 100000)}`,
  x: clamp(point.x, 0, 1),
  y: clamp(point.y, 0, 1),
  tolerance: settings?.tolerance ?? 25,
  contiguous: settings?.contiguous ?? true,
  feather: settings?.feather ?? 2,
  expand: settings?.expand ?? 0,
  opMode: settings?.opMode === 'subtract' ? 'subtract' : 'add',
  createdAt: Date.now(),
});

export const mapWorldPointToImageNormalized = ({ worldPoint, element }) => {
  if (!element) return null;
  const cx = element.x + element.w / 2;
  const cy = element.y + element.h / 2;
  const dx = worldPoint.x - cx;
  const dy = worldPoint.y - cy;
  const angle = -((element.rotation || 0) * Math.PI) / 180;
  const cos = Math.cos(angle);
  const sin = Math.sin(angle);
  const localX = dx * cos - dy * sin + element.w / 2;
  const localY = dx * sin + dy * cos + element.h / 2;
  if (localX < 0 || localY < 0 || localX > element.w || localY > element.h) {
    return null;
  }
  let u = localX / element.w;
  let v = localY / element.h;
  const crop = element.crop?.enabled
    ? element.crop
    : { x: 0, y: 0, w: 1, h: 1 };
  u = crop.x + u * crop.w;
  v = crop.y + v * crop.h;
  if (u < 0 || v < 0 || u > 1 || v > 1) return null;
  if (element.flipX) {
    u = 1 - u;
  }
  if (element.flipY) {
    v = 1 - v;
  }
  return {
    x: clamp(u, 0, 1),
    y: clamp(v, 0, 1),
  };
};

export const buildMagicMaskKey = ({
  ops = [],
  crop,
  flipX,
  flipY,
  width,
  height,
}) => {
  const cropKey = crop?.enabled
    ? `${crop.x.toFixed(3)}:${crop.y.toFixed(3)}:${crop.w.toFixed(3)}:${crop.h.toFixed(3)}`
    : 'full';
  const opsKey = ops
    .map((op) =>
      `${op.id}:${op.x.toFixed(3)}:${op.y.toFixed(3)}:${op.tolerance}:${op.contiguous ? 1 : 0}:${op.feather}:${op.expand}:${op.opMode}`,
    )
    .join('|');
  return `magic:${width}x${height}|crop:${cropKey}|flip:${flipX ? 1 : 0}${flipY ? 1 : 0}|ops:${opsKey}`;
};

const getPixelIndex = (x, y, width) => (y * width + x) * 4;

const getColorAt = (data, index) => [data[index], data[index + 1], data[index + 2]];

const colorDistance = (c1, c2) => {
  const dr = c1[0] - c2[0];
  const dg = c1[1] - c2[1];
  const db = c1[2] - c2[2];
  return Math.sqrt(dr * dr + dg * dg + db * db);
};

export const floodFillContiguous = ({
  data,
  width,
  height,
  startX,
  startY,
  threshold,
  cropRect,
}) => {
  const mask = new Uint8ClampedArray(width * height);
  const visited = new Uint8Array(width * height);
  const queue = [[startX, startY]];
  const startIndex = getPixelIndex(startX, startY, width);
  const targetColor = getColorAt(data, startIndex);
  const cropBounds = cropRect?.enabled
    ? {
        minX: Math.floor(cropRect.x * width),
        minY: Math.floor(cropRect.y * height),
        maxX: Math.ceil((cropRect.x + cropRect.w) * width),
        maxY: Math.ceil((cropRect.y + cropRect.h) * height),
      }
    : null;

  while (queue.length) {
    const [x, y] = queue.pop();
    if (x < 0 || y < 0 || x >= width || y >= height) continue;
    const idx = y * width + x;
    if (visited[idx]) continue;
    visited[idx] = 1;
    if (cropBounds) {
      if (x < cropBounds.minX || x >= cropBounds.maxX || y < cropBounds.minY || y >= cropBounds.maxY) {
        continue;
      }
    }
    const pixelIndex = getPixelIndex(x, y, width);
    const currentColor = getColorAt(data, pixelIndex);
    if (colorDistance(currentColor, targetColor) > threshold) continue;
    mask[idx] = 255;
    queue.push([x + 1, y]);
    queue.push([x - 1, y]);
    queue.push([x, y + 1]);
    queue.push([x, y - 1]);
  }

  return mask;
};

export const selectByScan = ({ data, width, height, targetColor, threshold, cropRect }) => {
  const mask = new Uint8ClampedArray(width * height);
  const cropBounds = cropRect?.enabled
    ? {
        minX: Math.floor(cropRect.x * width),
        minY: Math.floor(cropRect.y * height),
        maxX: Math.ceil((cropRect.x + cropRect.w) * width),
        maxY: Math.ceil((cropRect.y + cropRect.h) * height),
      }
    : null;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      if (cropBounds) {
        if (x < cropBounds.minX || x >= cropBounds.maxX || y < cropBounds.minY || y >= cropBounds.maxY) {
          continue;
        }
      }
      const pixelIndex = getPixelIndex(x, y, width);
      const currentColor = getColorAt(data, pixelIndex);
      if (colorDistance(currentColor, targetColor) <= threshold) {
        mask[y * width + x] = 255;
      }
    }
  }
  return mask;
};

const dilate = (mask, width, height) => {
  const result = new Uint8ClampedArray(mask.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x;
      if (mask[idx]) {
        result[idx] = 255;
        if (x > 0) result[idx - 1] = 255;
        if (x < width - 1) result[idx + 1] = 255;
        if (y > 0) result[idx - width] = 255;
        if (y < height - 1) result[idx + width] = 255;
      }
    }
  }
  return result;
};

const erode = (mask, width, height) => {
  const result = new Uint8ClampedArray(mask.length);
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      const idx = y * width + x;
      if (!mask[idx]) continue;
      const hasEmptyNeighbor =
        (x > 0 && !mask[idx - 1]) ||
        (x < width - 1 && !mask[idx + 1]) ||
        (y > 0 && !mask[idx - width]) ||
        (y < height - 1 && !mask[idx + width]);
      if (!hasEmptyNeighbor) {
        result[idx] = 255;
      }
    }
  }
  return result;
};

export const expandMask = (mask, width, height, amount) => {
  let result = mask;
  const steps = Math.min(Math.abs(amount), 10);
  if (amount > 0) {
    for (let i = 0; i < steps; i += 1) {
      result = dilate(result, width, height);
    }
  } else if (amount < 0) {
    for (let i = 0; i < steps; i += 1) {
      result = erode(result, width, height);
    }
  }
  return result;
};

export const featherMask = (mask, width, height, radius) => {
  if (radius <= 0) return mask;
  const result = new Uint8ClampedArray(mask.length);
  const r = Math.min(radius, 20);
  const size = r * 2 + 1;
  const area = size * size;
  for (let y = 0; y < height; y += 1) {
    for (let x = 0; x < width; x += 1) {
      let sum = 0;
      for (let ky = -r; ky <= r; ky += 1) {
        const yy = clamp(y + ky, 0, height - 1);
        for (let kx = -r; kx <= r; kx += 1) {
          const xx = clamp(x + kx, 0, width - 1);
          sum += mask[yy * width + xx];
        }
      }
      result[y * width + x] = sum / area;
    }
  }
  return result;
};

export const applySelectionToMask = (mask, selection, opMode) => {
  const next = new Uint8ClampedArray(mask.length);
  for (let i = 0; i < mask.length; i += 1) {
    if (opMode === 'subtract') {
      next[i] = Math.max(mask[i], selection[i]);
    } else {
      next[i] = Math.min(mask[i], 255 - selection[i]);
    }
  }
  return next;
};

export const buildMagicMask = ({ imageData, width, height, ops = [], cropRect, flipX, flipY }) => {
  if (!ops.length) return null;
  let mask = new Uint8ClampedArray(width * height);
  mask.fill(255);
  const data = imageData.data;

  ops.forEach((op) => {
    const tolerance = clamp(op.tolerance ?? 25, 0, 100);
    const threshold = (tolerance / 100) * 255;
    const opX = clamp(op.x ?? 0.5, 0, 1);
    const opY = clamp(op.y ?? 0.5, 0, 1);
    const px = Math.round((flipX ? 1 - opX : opX) * width);
    const py = Math.round((flipY ? 1 - opY : opY) * height);
    const ix = clamp(px, 0, width - 1);
    const iy = clamp(py, 0, height - 1);
    const targetColor = getColorAt(data, getPixelIndex(ix, iy, width));

    let selection = op.contiguous
      ? floodFillContiguous({ data, width, height, startX: ix, startY: iy, threshold, cropRect })
      : selectByScan({ data, width, height, targetColor, threshold, cropRect });

    selection = expandMask(selection, width, height, clamp(op.expand ?? 0, -10, 10));
    selection = featherMask(selection, width, height, clamp(op.feather ?? 0, 0, 20));

    mask = applySelectionToMask(mask, selection, op.opMode);
  });

  return mask;
};
